import { useState } from 'react';
import { AlertTriangle, ShieldCheck } from 'lucide-react';
import { FileInput } from '@/Components/shared/FileInput';

interface VehicleCheckModalProps {
  isOpen: boolean;
  onConfirm: (file: File) => void;
  onCancel: () => void;
  isUploading?: boolean;
}

/**
 * Modal bloquant de vérification véhicule obligatoire.
 * Affiché lors de la création de contrat quand `mandatoryVehicleCheck` est activé.
 * L'utilisateur doit uploader un rapport de vérification avant de continuer.
 */
export const VehicleCheckModal = ({
  isOpen,
  onConfirm,
  onCancel,
  isUploading = false,
}: VehicleCheckModalProps) => {
  const [file, setFile] = useState<File | null>(null);

  if (!isOpen) return null;

  const handleConfirm = () => {
    if (file) {
      onConfirm(file);
    }
  };

  return (
    <div className="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center tw-bg-black tw-bg-opacity-50">
      <div className="tw-bg-white tw-rounded-xl tw-shadow-2xl tw-max-w-lg tw-w-full tw-mx-4 tw-overflow-hidden">
        {/* Header */}
        <div className="tw-bg-amber-50 tw-border-b tw-border-amber-200 tw-px-6 tw-py-4 tw-flex tw-items-center tw-gap-3">
          <div className="tw-p-2 tw-bg-amber-100 tw-rounded-lg">
            <ShieldCheck className="tw-w-6 tw-h-6 tw-text-amber-600" />
          </div>
          <div>
            <h3 className="tw-text-lg tw-font-semibold tw-text-gray-900 tw-mb-0">
              Vérification du véhicule
            </h3>
            <p className="tw-text-sm tw-text-amber-700 tw-mb-0">
              Étape obligatoire avant la création du contrat
            </p>
          </div>
        </div>

        {/* Body */}
        <div className="tw-px-6 tw-py-5">
          {/* Texte de confirmation légal */}
          <div className="tw-bg-gray-50 tw-border tw-border-gray-200 tw-rounded-lg tw-p-4 tw-mb-5">
            <div className="tw-flex tw-gap-3">
              <AlertTriangle className="tw-w-5 tw-h-5 tw-text-amber-500 tw-flex-shrink-0 tw-mt-0.5" />
              <p className="tw-text-sm tw-text-gray-700 tw-mb-0 tw-leading-relaxed">
                Je confirme qu'un check du véhicule a été effectué par un professionnel,
                conformément au process de vente défini dans le contrat de franchise.
                Le rapport ci-joint en apporte la preuve.
              </p>
            </div>
          </div>

          {/* Upload du rapport */}
          <FileInput
            value={file}
            onChange={setFile}
            label="Rapport de vérification"
            accept=".pdf,.jpg,.jpeg,.png"
            maxSize={10}
            required
            description="Formats acceptés : PDF, JPG, JPEG, PNG \u2014 Max 10 Mo"
            showPreview
            showActions={false}
          />
        </div>

        {/* Footer */}
        <div className="tw-px-6 tw-py-4 tw-bg-gray-50 tw-border-t tw-border-gray-200 tw-flex tw-justify-end tw-gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={isUploading}
            className="tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-gray-700 tw-bg-white tw-border tw-border-gray-300 tw-rounded-lg hover:tw-bg-gray-50 tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!file || isUploading}
            className="tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-white tw-bg-affinicar-primary tw-rounded-lg hover:tw-bg-affinicar-primary-dark tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-flex tw-items-center tw-gap-2"
          >
            {isUploading && (
              <div className="tw-animate-spin tw-w-4 tw-h-4 tw-border-2 tw-border-white tw-border-t-transparent tw-rounded-full" />
            )}
            Je confirme
          </button>
        </div>
      </div>
    </div>
  );
};
